import React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '@/shared/utils/cn';
import type { LegacyIconProps } from '@/shared/types/legacyIcon';
import type { IconFontColor, IconFontSize, VisualSize } from '@/shared/types/types';
import { backgroundSizeValues, paddingValues, sizeMap } from '@/modules/Icon/constants/size';
import * as OutlineIcons from '@/shared/modules/Icons/componentes/outline';
import * as FillIcons from '@/shared/modules/Icons/componentes/fill';

const LEGACY_ICONS = {
  ...OutlineIcons,
  ...FillIcons,
} as const;

type LegacySvgComponent = React.ComponentType<React.SVGProps<SVGSVGElement>>;

/** Mismo mapa semántico que `IconFont` para fondos del chip. */
const ICON_BACKGROUND_SEMANTIC_CLASS: Record<string, string> = {
  white: 'ui-bg-fill',
  fill: 'ui-bg-fill',
  default: 'ui-bg-surface-default',
  current: 'ui-bg-surface-default',
  primary: 'ui-bg-surface-default',
  brand: 'ui-bg-surface-brand',
  secondary: 'ui-bg-surface-secondary',
  tertiary: 'ui-bg-surface-tertiary',
  success: 'ui-bg-surface-success',
  warning: 'ui-bg-surface-warning',
  danger: 'ui-bg-surface-danger',
  info: 'ui-bg-surface-info',
  neutral: 'ui-bg-surface-secondary',
  disabled: 'ui-bg-surface-disabled',
  red: 'ui-bg-surface-all-colors-red',
  orange: 'ui-bg-surface-all-colors-orange',
  yellow: 'ui-bg-surface-all-colors-yellow',
  lime: 'ui-bg-surface-all-colors-lime',
  green: 'ui-bg-surface-all-colors-green',
  cyan: 'ui-bg-surface-all-colors-cyan',
  azure: 'ui-bg-surface-all-colors-azure',
  blue: 'ui-bg-surface-all-colors-blue',
  violet: 'ui-bg-surface-all-colors-violet',
  magenta: 'ui-bg-surface-all-colors-magenta',
  rose: 'ui-bg-surface-all-colors-rose',
};

const legacyIconColorVariants = cva('ui-transition-all ui-duration-200 ui-ease-in-out', {
  variants: {
    color: {
      current: '',
      disabled: 'ui-text-disabled',
      default:
        'ui-text-icon group-disabled:ui-text-icon-disabled group-hover:ui-text-icon-hover group-active:ui-text-icon-pressed',
      brand:
        'ui-text-icon-brand group-hover:ui-text-icon-brand-hover group-active:ui-text-icon-brand-pressed group-disabled:ui-text-icon-disabled',
      primary:
        '!ui-text-primary group-disabled:!ui-text-disabled group-hover:!ui-text-primary group-active:!ui-text-primary',
      secondary:
        'ui-text-icon-secondary group-disabled:ui-text-icon-disabled group-hover:ui-text-icon-secondary-hover group-active:ui-text-icon-secondary-pressed',
      tertiary:
        'ui-text-icon-tertiary group-disabled:ui-text-icon-disabled group-hover:ui-text-icon-tertiary-hover group-active:ui-text-icon-tertiary-pressed',
      success:
        'ui-text-icon-success group-disabled:ui-text-icon-disabled group-hover:ui-text-icon-success-hover group-active:ui-text-icon-success-pressed',
      warning:
        'ui-text-icon-warning group-disabled:ui-text-icon-disabled group-hover:ui-text-icon-warning-hover group-active:ui-text-icon-warning-pressed',
      danger:
        'ui-text-icon-danger group-disabled:ui-text-icon-disabled group-hover:ui-text-icon-danger-hover group-active:ui-text-icon-danger-pressed',
      info: 'ui-text-icon-info group-disabled:ui-text-icon-disabled group-hover:ui-text-icon-info-hover group-active:ui-text-icon-info-pressed',
      neutral:
        'ui-text-icon-secondary group-disabled:ui-text-icon-disabled group-hover:ui-text-icon-secondary-hover group-active:ui-text-icon-secondary-pressed',
      red: 'ui-text-icon-all-colors-red group-disabled:ui-text-icon-disabled group-hover:ui-text-icon-all-colors-red-hover group-active:ui-text-icon-all-colors-red-pressed',
      orange:
        'ui-text-icon-all-colors-orange group-disabled:ui-text-icon-disabled group-hover:ui-text-icon-all-colors-orange-hover group-active:ui-text-icon-all-colors-orange-pressed   ',
      yellow:
        'ui-text-icon-all-colors-yellow group-disabled:ui-text-icon-disabled group-hover:ui-text-icon-all-colors-yellow-hover group-active:ui-text-icon-all-colors-yellow-pressed',
      lime: 'ui-text-icon-all-colors-lime group-disabled:ui-text-icon-disabled group-hover:ui-text-icon-all-colors-lime-hover group-active:ui-text-icon-all-colors-lime-pressed',
      green:
        'ui-text-icon-all-colors-green group-disabled:ui-text-icon-disabled group-hover:ui-text-icon-all-colors-green-hover group-active:ui-text-icon-all-colors-green-pressed',
      cyan: 'ui-text-icon-all-colors-cyan group-disabled:ui-text-icon-disabled group-hover:ui-text-icon-all-colors-cyan-hover group-active:ui-text-icon-all-colors-cyan-pressed',
      azure:
        'ui-text-icon-all-colors-azure group-disabled:ui-text-icon-disabled group-hover:ui-text-icon-all-colors-azure-hover group-active:ui-text-icon-all-colors-azure-pressed',
      blue: 'ui-text-icon-all-colors-blue group-disabled:ui-text-icon-disabled group-hover:ui-text-icon-all-colors-blue-hover group-active:ui-text-icon-all-colors-blue-pressed',
      violet:
        'ui-text-icon-all-colors-violet group-disabled:ui-text-icon-disabled group-hover:ui-text-icon-all-colors-violet-hover group-active:ui-text-icon-all-colors-violet-pressed',
      magenta:
        'ui-text-icon-all-colors-magenta group-disabled:ui-text-icon-disabled group-hover:ui-text-icon-all-colors-magenta-hover group-active:ui-text-icon-all-colors-magenta-pressed',
      rose: 'ui-text-icon-all-colors-rose group-disabled:ui-text-icon-disabled group-hover:ui-text-icon-all-colors-rose-hover group-active:ui-text-icon-all-colors-rose-pressed',
      white: 'ui-text-white group-disabled:ui-text-icon-disabled',
    },
  },
} as const);

export function LegacyIcon({
  name,
  size = 'sm',
  color = 'current',
  className,
  background = false,
  backgroundColor,
  backgroundSize = 'md',
  padding = 'none',
  style,
  onClick,
  colorDuotonePrimary: _colorDuotonePrimary,
  colorDuotoneSecondary: _colorDuotoneSecondary,
  opacityDuotonePrimary: _opacityDuotonePrimary,
  opacityDuotoneSecondary: _opacityDuotoneSecondary,
}: LegacyIconProps) {
  const IconComponent = LEGACY_ICONS[name as keyof typeof LEGACY_ICONS] as
    | LegacySvgComponent
    | undefined;

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (onClick) {
      e.stopPropagation();
      onClick(e);
    }
  };

  const getBackgroundClass = () => {
    if (!background) return '';
    if (backgroundColor) {
      if (backgroundColor.startsWith('ui-')) {
        return `ui-rounded-control-pill ${backgroundColor}`;
      }
      const semantic = ICON_BACKGROUND_SEMANTIC_CLASS[backgroundColor];
      if (semantic) {
        return `ui-rounded-control-pill ${semantic}`;
      }
      return `ui-rounded-control-pill ui-bg-surface-${backgroundColor}`;
    }
    return 'ui-rounded-control-pill ui-bg-surface-all-colors-blue';
  };

  const getPaddingClass = () => {
    if (padding !== 'none') {
      return paddingValues[padding];
    }
    if (background) {
      return backgroundSizeValues[backgroundSize as VisualSize];
    }
    return '';
  };

  const px = sizeMap[size as IconFontSize] ?? sizeMap.sm;
  const colorClassNames = cn(legacyIconColorVariants({ color: color as IconFontColor }), className);

  const containerBaseClasses = background
    ? 'ui-flex ui-items-center ui-justify-center ui-shrink-0 ui-w-fit ui-h-fit'
    : 'ui-inline-block ui-flex ui-items-center ui-justify-center';

  if (!IconComponent) {
    return null;
  }

  const svg = (
    <IconComponent
      width={px}
      height={px}
      className={colorClassNames}
      style={style as React.CSSProperties}
    />
  );

  if (onClick) {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={handleClick}
        className={cn(
          'ui-imj',
          containerBaseClasses,
          'ui-cursor-pointer ui-transition-all ui-duration-200',
          getBackgroundClass(),
          getPaddingClass(),
          'hover:ui-bg-[rgba(0,0,0,0.15)] hover:ui-shadow-[0_4px_12px_rgba(0,0,0,0.15)]'
        )}
      >
        {svg}
      </div>
    );
  }

  return (
    <div className={cn('ui-imj', containerBaseClasses, getBackgroundClass(), getPaddingClass())}>
      {svg}
    </div>
  );
}
